import { getAuthenticator } from './authenticator';
import * as utils from './utils';
/**
 * For request:
 * 1) get token from authenticator
 * 2) set authorization header
 * 3) return config
 */
export const registerAuthInterceptor = (instance) => {
    const interceptorId = instance.interceptors.request.use(async (config) => {
        const { getToken } = getAuthenticator();
        const accessToken = await getToken();
        config.headers.authorization = `Bearer ${accessToken}`;
        return config;
    });
    return () => {
        instance.interceptors.request.eject(interceptorId);
    };
};
/**
 * For response error:
 * 1) if it's not an unauthorized error or we've already attempted to refresh the token rethrow the error
 * 2) try to refresh the token
 * 3) if it was first retry attempt and it was a success - retry original request otherwise rethrow the error
 *
 * See `axios.spec.ts` to get more info
 */
export const registerTokenExpiredErrorInterceptor = (instance) => {
    const interceptorId = instance.interceptors.response.use(undefined, async (error) => {
        const { maybeRefreshToken } = getAuthenticator();
        // handle Unauthorized error
        if (error?.response?.status === 401) {
            const { config: requestConfig } = error;
            // re-throw if refresh has already been attempted for this request
            if (requestConfig.__CONVERSE__attemptedTokenForceRefresh) {
                throw error;
            }
            requestConfig.__CONVERSE__attemptedTokenForceRefresh = true;
            try {
                const authHeader = requestConfig.headers.has('Authorization')
                    ? requestConfig.headers.get('Authorization')
                    : undefined;
                const restoredAccessToken = authHeader?.toString().replace('Bearer ', '');
                await maybeRefreshToken(restoredAccessToken);
            }
            catch (tokenRefreshError) {
                // re-throw error if force refresh failed
                throw error;
            }
            // try making same request again as the token must be ok now
            return instance(requestConfig);
        }
        // handle rate limiter rejections error
        if (error?.response?.status === 429) {
            // get delay from a response header
            const maybeNaNDelayInSeconds = Number(typeof error.response.headers.get === 'function'
                ? error.response.headers.get('retry-after')
                : error.response.headers['retry-after']);
            // if delay is not a number, use default value of 3 seconds
            const delay = isNaN(maybeNaNDelayInSeconds) ? 3_000 : maybeNaNDelayInSeconds * 1_000;
            // wait before retrying
            await utils.waitAsync(delay);
            return instance(error.config);
        }
        // re-throw other errors
        throw error;
    });
    return () => {
        instance.interceptors.response.eject(interceptorId);
    };
};
//# sourceMappingURL=axiosInterceptors.js.map